import { useState } from 'react';
import { Bell, ChevronDown, ChevronUp } from 'lucide-react';
import type { Issue } from '@/types';
import { useIssueStore } from '@/stores';

interface CalendarEventItem {
  issueId: string;
  issueSequence: number;
  issueDescription: string;
  eventType: 'task' | 'st-breakpoint' | 'lt-breakpoint';
  eventDate: string;
  owner: string;
  content: string;
  taskId?: string;
  actionItemId?: string;
  isDelayed?: boolean;
}

interface IssuesCalendarEventsProps {
  productIssues: Issue[];
  productionIssues: Issue[];
  selectedDate: string;
  onEventClick: (event: CalendarEventItem) => void;
}

interface GroupedEvent {
  issueId: string;
  issueSequence: number;
  issueDescription: string;
  owner: string;
  events: CalendarEventItem[];
}

export function IssuesCalendarEvents({ productIssues, productionIssues, selectedDate, onEventClick }: IssuesCalendarEventsProps) {
  const [expandedIssues, setExpandedIssues] = useState<Set<string>>(new Set());
  const [confirmedEvents, setConfirmedEvents] = useState<Record<string, 'yes' | 'no' | null>>({});
  const { completeTask, delayTask } = useIssueStore();

  const allIssues = [...productIssues, ...productionIssues];

  const eventsByDate = new Map<string, CalendarEventItem[]>();

  allIssues.forEach(issue => {
    issue.planTasks.forEach(task => {
      const eventDate = task.newPlannedDate || task.plannedDate;
      if (eventDate) {
        const dateKey = eventDate;
        if (!eventsByDate.has(dateKey)) {
          eventsByDate.set(dateKey, []);
        }
        eventsByDate.get(dateKey)!.push({
          issueId: issue.id,
          issueSequence: issue.sequence,
          issueDescription: issue.description,
          eventType: 'task',
          eventDate: eventDate,
          owner: issue.owner || 'Unknown',
          content: task.content || task.description || task.name || issue.description,
          taskId: task.id,
          isDelayed: !!task.newPlannedDate,
        });
      }
    });

    issue.actionItems?.forEach((action, idx) => {
      const isBpNg = action.status === 'bp-ng';
      const isDelayedComplete = action.status === 'delayed-complete';
      const isCompleted = action.status === 'completed';
      
      if (isCompleted || isDelayedComplete) return;
      
      let effectiveDate = action.breakpointDate;
      let isDelayed = false;
      
      if (isBpNg && action.delayHistory && action.delayHistory.length > 0) {
        const latestDelay = action.delayHistory[action.delayHistory.length - 1];
        effectiveDate = latestDelay.newPlannedDate;
        isDelayed = true;
      }
      
      if (effectiveDate) {
        const dateKey = effectiveDate;
        if (!eventsByDate.has(dateKey)) {
          eventsByDate.set(dateKey, []);
        }
        eventsByDate.get(dateKey)!.push({
          issueId: issue.id,
          issueSequence: issue.sequence,
          issueDescription: issue.description,
          eventType: action.actionType === 'short_term' ? 'st-breakpoint' : 'lt-breakpoint',
          eventDate: effectiveDate,
          owner: issue.owner || 'Unknown',
          content: action.content || issue.description,
          actionItemId: action.id,
          isDelayed: isDelayed,
        });
      }
    });
  });

  const selectedDateEvents = eventsByDate.get(selectedDate) || [];

  const groupedEvents: GroupedEvent[] = selectedDateEvents.reduce((acc, event) => {
    const existing = acc.find(g => g.issueId === event.issueId);
    if (existing) {
      existing.events.push(event);
    } else {
      acc.push({
        issueId: event.issueId,
        issueSequence: event.issueSequence,
        issueDescription: event.issueDescription,
        owner: event.owner,
        events: [event],
      });
    }
    return acc;
  }, [] as GroupedEvent[]);

  const toggleExpand = (issueId: string) => {
    const newExpanded = new Set(expandedIssues);
    if (newExpanded.has(issueId)) {
      newExpanded.delete(issueId);
    } else {
      newExpanded.add(issueId);
    }
    setExpandedIssues(newExpanded);
  };

  const handleConfirm = async (eventKey: string, confirmed: 'yes' | 'no', event: CalendarEventItem) => {
    if (event.eventType !== 'task' || !event.taskId) return;

    const today = new Date().toISOString().split('T')[0];

    if (confirmed === 'yes') {
      await completeTask(event.taskId, { actualDate: today });
    } else {
      await delayTask(event.taskId, {
        delayReason: 'User delayed from Reminder',
        newPlannedDate: today,
        recordedBy: 'current-user',
      });
    }

    setConfirmedEvents(prev => ({
      ...prev,
      [eventKey]: prev[eventKey] === confirmed ? null : confirmed,
    }));
  };

  const getEventTypeLabel = (eventType: 'task' | 'st-breakpoint' | 'lt-breakpoint'): string => {
    switch (eventType) {
      case 'task':
        return '工作计划';
      case 'st-breakpoint':
        return '短期措施';
      case 'lt-breakpoint':
        return '长期措施';
    }
  };

  const getEventTypeColor = (eventType: 'task' | 'st-breakpoint' | 'lt-breakpoint'): string => {
    switch (eventType) {
      case 'task':
        return '#4b5563';
      case 'st-breakpoint':
        return '#60A5FA';
      case 'lt-breakpoint':
        return '#2563EB';
    }
  };

  return (
    <div className="p-3 rounded-xl border border-gray-100 bg-white h-[300px] flex flex-col">
      <div className="flex items-center gap-2 mb-3">
        <div className="p-2 rounded-lg">
          <Bell className="w-4 h-4" style={{ color: '#6B7280' }} />
        </div>
        <span className="text-sm font-medium text-gray-600">Reminder</span>
      </div>

      <div className="space-y-1 flex-1 overflow-y-auto">
        {groupedEvents.length === 0 ? (
          <p className="text-xs text-gray-500">No events</p>
        ) : (
          groupedEvents.map((group) => {
            const isExpanded = expandedIssues.has(group.issueId);
            return (
              <div key={group.issueId}>
                <div
                  className="flex items-center justify-between py-1 hover:bg-gray-50 transition-all cursor-pointer"
                  onClick={() => toggleExpand(group.issueId)}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <span className="text-xs font-medium text-gray-800 whitespace-nowrap">
                      Issue #{group.issueSequence}
                    </span>
                    <span className="text-xs text-gray-600 truncate">
                      {group.issueDescription}
                    </span>
                  </div>
                  <button className="p-1 hover:bg-gray-100 rounded transition-colors">
                    {isExpanded ? (
                      <ChevronUp className="w-3 h-3 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-gray-500" />
                    )}
                  </button>
                </div>

                {isExpanded && (
                  <div className="pl-2 space-y-1">
                    {group.events.map((event, idx) => {
                      const eventKey = `${event.issueId}-${idx}`;
                      const confirmation = confirmedEvents[eventKey];
                      return (
                        <div
                          key={`${event.issueId}-${idx}`}
                          className="py-1 cursor-pointer hover:bg-gray-100 rounded px-1 -mx-1 transition-colors"
                          onClick={() => onEventClick(event)}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <span
                                className="px-1.5 py-0.5 text-xs font-medium rounded whitespace-nowrap"
                                style={{
                                  backgroundColor: getEventTypeColor(event.eventType),
                                  color: 'white',
                                }}
                              >
                                {getEventTypeLabel(event.eventType)}
                              </span>
                              {event.isDelayed && (
                                <span className="px-1.5 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-600">
                                  延期
                                </span>
                              )}
                              <span className="text-xs text-gray-700">
                                {event.content}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
